import { todayWithSystemTimeZone } from '@magicdoorinc/date';
import { createTriggerResource } from '@magicdoorinc/ui';
import { createSignal, createEffect } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { ChatRepository } from '~/repositories/chatRepository';
import { useLatestMessage } from './ChatUpdateStore';
import { useGlobalChat } from './GlobalChatContext';
import type { Accessor, Resource } from 'solid-js';
import type { ChatFilter, ChatSearchFilter, UnreadMessageFilter } from '~/repositories/chatRepository';
import type { HydratedMagicChatDto, PropertyManagerDto, UnreadMessagePaginationDto } from '~/swagger/portal/Api';

interface ChatsListContextValue {
  fetchChats: () => Promise<void>;
  chats: Accessor<HydratedMagicChatDto[]>;
  onChatAdded: (chat: HydratedMagicChatDto) => void;
  isLoading: Accessor<boolean>;
  error: Accessor<string | undefined>;
  chatCount: Accessor<number>;
  clearUnreadMessages: (chatId: string) => void;
  updateChatPinnedStatus: (chatId: string, pinned: boolean) => void;
  updateChatHumanEngagement: (chatId: string, requested: boolean, requestedAt?: string | null) => void;
  updateChatAssignedPropertyManager: (chatId: string, assignedPropertyManager: PropertyManagerDto) => void;
  setChatAssignment: (
    chatId: string,
    assignedPropertyManager: PropertyManagerDto | null,
    assignedByPropertyManager: PropertyManagerDto | null,
    assignedAt: string | null
  ) => void;
  addNewCreatedChat: (newChat: any) => void;
  filtered: Resource<UnreadMessagePaginationDto>;
  setFilter: (trigger: UnreadMessageFilter) => UnreadMessageFilter;
  chatFiltered: Resource<HydratedMagicChatDto[]>;
  setChatFilter: (trigger: ChatFilter) => ChatFilter;
  refetch: (info?: unknown) => UnreadMessagePaginationDto | Promise<UnreadMessagePaginationDto | undefined> | null | undefined;
  chatRefetch: (info?: unknown) => HydratedMagicChatDto[] | Promise<HydratedMagicChatDto[] | undefined> | null | undefined;
  unarchiveChat: (chatId: string) => Promise<void>;
  searchChats: (filters: ChatSearchFilter) => Promise<UnreadMessagePaginationDto>;
}

export const [ChatsListProvider, useChatsList] = createMagicDoorContext<ChatsListContextValue>('ChatsList', () => {
  const repo = new ChatRepository();

  const [chats, setChats] = createSignal<HydratedMagicChatDto[]>([]);
  const [isLoading, setIsLoading] = createSignal<boolean>(false);
  const [error, setError] = createSignal<string | undefined>(undefined);
  const [chatCount, setChatCount] = createSignal<number>(0);
  const [filtered, setFilter, actions] = createTriggerResource((filter: UnreadMessageFilter) => repo.getUnreadMessage(filter));
  const [chatFiltered, setChatFilter, chatActions] = createTriggerResource((filter: ChatFilter) => repo.getChats(filter));

  const fetchChats = async () => {
    setIsLoading(true);
    try {
      const response = await repo.getChats();

      const list: HydratedMagicChatDto[] = response.map(map);

      setChats(list);
      setChatCount(list.length);
      setError(undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsLoading(false);
    }
  };

  const addNewCreatedChat = (newChat: any) => {
    setChats((prevChats) => [...prevChats, newChat]);
  };

  const latestMessage = useLatestMessage();

  createEffect(() => {
    const notification = latestMessage();
    if (notification) {
      fetchChats();
    }
  });

  const map = (chat: HydratedMagicChatDto): HydratedMagicChatDto => {
    return chat;
  };

  const onChatAdded = (chat: HydratedMagicChatDto) => {
    setChats((prevChats) => [map(chat), ...prevChats]);
    setChatCount((count) => count + 1);
  };

  const clearUnreadMessages = (chatId: string) => {
    setChats((chats) =>
      chats.map((chat) => {
        if (chat.id === chatId) {
          return { ...chat, unreadMessages: 0 };
        }
        return chat;
      })
    );
  };

  const updateChatPinnedStatus = (chatId: string, pinned: boolean) => {
    setChats((chats) =>
      chats.map((chat) => {
        if (chat.id === chatId) {
          return { ...chat, pinned };
        }
        return chat;
      })
    );
  };

  const updateChatHumanEngagement = (chatId: string, requested: boolean, requestedAt?: string | null) => {
    setChats((chats) =>
      chats.map((chat) => {
        if (chat.id === chatId) {
          return { ...chat, humanEngagementRequested: requested ? (requestedAt ?? todayWithSystemTimeZone().toISOString()) : undefined };
        }
        return chat;
      })
    );
  };

  const updateChatAssignedPropertyManager = (chatId: string, assignedPropertyManager: PropertyManagerDto) => {
    setChats((chats) =>
      chats.map((chat) => {
        if (chat.id === chatId) {
          return { ...chat, assignedPropertyManager };
        }
        return chat;
      })
    );
  };

  const setChatAssignment = (
    chatId: string,
    assignedPropertyManager: PropertyManagerDto | null,
    assignedByPropertyManager: PropertyManagerDto | null,
    assignedAt: string | null
  ) => {
    setChats((chats) =>
      chats.map((chat) => {
        if (chat.id === chatId) {
          return {
            ...chat,
            assignedPropertyManager: assignedPropertyManager ?? undefined,
            assignedByPropertyManager: assignedByPropertyManager ?? undefined,
            assignedAt: assignedAt ?? undefined,
          };
        }
        return chat;
      })
    );
  };

  const unarchiveChat = async (chatId: string) => {
    await repo.unarchiveChat(chatId);
    chatActions.refetch();
  };

  const searchChats = async (filters: ChatSearchFilter) => {
    const response = await repo.searchChat(filters);
    return response;
  };

  useGlobalChat().registerReconnectedCallback(() => {
    void fetchChats();
    actions.refetch();
  });

  return {
    fetchChats,
    chats,
    onChatAdded,
    isLoading,
    error,
    chatCount,
    clearUnreadMessages,
    updateChatPinnedStatus,
    updateChatHumanEngagement,
    updateChatAssignedPropertyManager,
    setChatAssignment,
    addNewCreatedChat,
    filtered: filtered,
    setFilter: (filter: UnreadMessageFilter) => setFilter(filter),
    refetch: actions.refetch,
    chatFiltered,
    setChatFilter,
    chatRefetch: chatActions.refetch,
    unarchiveChat,
    searchChats,
  };
});
